import type { ButtonProps, VanillaLinkProps } from '@_/ui';
import type * as React from 'react';
import { HiDotsHorizontal } from 'react-icons/hi';
import { LuChevronDown } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';
import { buttonCommonI18n } from '@_/i18n-common';
import {
  Button,
  Menu,
  MenuItem,
  MenuItemIcon,
  MenuItemLabel,
  MenuPopup,
  MenuTrigger,
  Tooltip
} from '@_/ui';

export type ActionGroupItem = {
  canCompact?: boolean;
  color?: 'default' | 'primary' | 'danger';
  disabled?: boolean;
  icon: React.ReactNode;
  id: string;
  label: React.ReactNode;
  onClick?: () => void;
  renderLink?: (props: VanillaLinkProps) => React.ReactNode;
};

export type ActionGroupProps = {
  actions: ActionGroupItem[];
  compact?: boolean;
  maxInlineActions?: number;
  showOverflowLabel?: boolean;
  size?: ButtonProps['size'];
  variant?: Exclude<ButtonProps['variant'], 'plain' | 'subtle'>;
};

export function ActionGroup({
  actions,
  compact = false,
  maxInlineActions = 2,
  showOverflowLabel = false,
  size,
  variant
}: ActionGroupProps) {
  if (!actions.length) {
    return;
  }

  const inlineActions = actions.slice(0, maxInlineActions);
  const overflowActions = actions.slice(maxInlineActions);

  return (
    <div className="flex items-center gap-x-2">
      {inlineActions.map((action, index) =>
        renderButton({
          action,
          compact:
            compact ||
            (index > 0 && !!overflowActions.length && !showOverflowLabel && action.canCompact),
          size,
          variant
        })
      )}

      {!!overflowActions.length && (
        <>
          {overflowActions.length === 1 && (compact || overflowActions[0].canCompact) ? (
            renderButton({
              action: overflowActions[0],
              compact: compact || overflowActions[0].canCompact,
              size,
              variant
            })
          ) : (
            <Menu>
              {showOverflowLabel ? (
                <MenuTrigger
                  render={<Button iconRight={<LuChevronDown />} size={size} variant={variant} />}
                >
                  <FormattedMessage {...buttonCommonI18n.actions} />
                </MenuTrigger>
              ) : (
                <MenuTrigger
                  render={<Button icon={<HiDotsHorizontal />} size={size} variant={variant} />}
                />
              )}
              <MenuPopup align="end" side="bottom">
                {overflowActions.map(action => (
                  <MenuItem
                    key={action.id}
                    color={action.color === 'danger' ? 'danger' : 'default'}
                    disabled={action.disabled}
                    onClick={action.onClick}
                    renderLink={action.renderLink}
                  >
                    <MenuItemIcon>{action.icon}</MenuItemIcon>
                    <MenuItemLabel>{action.label}</MenuItemLabel>
                  </MenuItem>
                ))}
              </MenuPopup>
            </Menu>
          )}
        </>
      )}
    </div>
  );
}

function renderButton(
  data: Pick<ActionGroupProps, 'compact' | 'size' | 'variant'> & {
    action: ActionGroupItem;
  }
) {
  const { action, compact, size, variant } = data;

  return (
    <Tooltip key={action.id} disabled={!compact} message={action.label}>
      <Button
        children={!compact ? action.label : undefined}
        color={action.color}
        iconLeft={!compact ? action.icon : undefined}
        disabled={action.disabled}
        icon={compact ? action.icon : undefined}
        onClick={action.onClick}
        renderLink={action.renderLink}
        size={size}
        variant={variant}
      />
    </Tooltip>
  );
}
